import { useRef, useState, useCallback, useMemo } from "react";
import { View, Text, Linking } from "react-native";
import { WebView } from "react-native-webview";
import { os, logError } from "../platform";
import { fontFamilyStyle } from "../utils/styles/typography";

const DOMPurify = os === "web" ? require("dompurify") : null;

const HTML_REGEX = /<\/?[a-z][\s\S]*>/i;
const DEFAULT_WEBVIEW_HEIGHT = 30;
const WEBVIEW_HEIGHT_PADDING = 10;
const WEB_LINE_HEIGHT = 1.5;
const NATIVE_LINE_HEIGHT = 23;

const ContainsHTML = ({ text, style }) => {
  const webViewRef = useRef(null);
  const [webViewHeight, setWebViewHeight] = useState(DEFAULT_WEBVIEW_HEIGHT);

  const hasHTML = HTML_REGEX.test(text);

  const combinedStyle = {
    ...fontFamilyStyle,
    ...style,
    lineHeight: hasHTML && os === "web" ? WEB_LINE_HEIGHT : NATIVE_LINE_HEIGHT,
  };

  const handleLayout = useCallback(() => {
    if (os === "android") {
      webViewRef.current?.injectJavaScript(`
        const height = Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight
        ) + ${WEBVIEW_HEIGHT_PADDING};
        window.ReactNativeWebView.postMessage(height.toString());
      `);
    }
  }, []);

  const sanitizedHTML =
    os === "web" && DOMPurify
      ? DOMPurify.sanitize(text, {
          ALLOWED_TAGS: ["p", "table", "tr", "td", "img", "strong", "em", "br", "div", "ul", "ol", "li", "a"],
          ALLOWED_ATTR: ["src", "style", "alt", "role", "width", "height", "aria-label", "aria-live", "href", "target"],
          ALLOW_DATA_ATTR: false,
        })
      : text;

  const injectedJS = `
    function sendHeight() {
      const height = document.documentElement.scrollHeight || document.body.scrollHeight;
      const padding = ${os === "ios" ? 10 : 0};
      window.ReactNativeWebView.postMessage((height + padding).toString());
    }
  
    if (document.readyState === 'complete') {
      sendHeight();
    } else {
      const handleLoad = () => {
        sendHeight();
        window.removeEventListener('load', handleLoad);
      };
      window.addEventListener('load', handleLoad);
    }
    true;
  `;

  const shouldStartLoadWithRequest = (request) => {
    // Check if the URL should be opened externally (e.g., if it's not part of your app's domain)
    if (request.url.startsWith("http") || request.url.startsWith("https") || request.url.startsWith("mailto:")) {
      Linking.openURL(request.url).catch((err) =>
        logError("webview_link_open_failed", {
          message: err.message,
          url: request.url,
        })
      );
      return false; // Prevents the WebView from loading the URL
    }
    return true; // Allows the WebView to load the URL
  };

  const htmlContent = useMemo(
    () => `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-size: ${style?.fontSize || 16}px;
            line-height: ${style?.lineHeight || 26}px;
            font-family: ${fontFamilyStyle.fontFamily};
            color: ${style?.color || "#000"}; 
            font-weight: ${style?.fontWeight || "normal"};
            margin: 0;
            padding-top: ${style?.paddingTop || 0}px;
          }
          p {
            margin: 0 0 0 0;
          }
        </style>
      </head>
      <body>
        ${sanitizedHTML}
      </body>
    </html>
  `,
    [style?.fontSize, style?.lineHeight, style?.color, style?.fontWeight, style?.paddingTop, sanitizedHTML]
  );

  return (
    <View>
      {os === "web" && hasHTML ? (
        <div
          style={combinedStyle}
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
          role="article"
          aria-label="Content"
        />
      ) : os !== "web" && hasHTML ? (
        <View onLayout={handleLayout}>
          <WebView
            ref={webViewRef}
            style={[
              style,
              {
                height: webViewHeight,
              },
            ]}
            limitsNavigationsToAppBoundDomains={true}
            source={{ html: htmlContent }}
            originWhitelist={["*"]}
            javaScriptEnabled={true}
            scrollEnabled={false}
            injectedJavaScript={injectedJS}
            onShouldStartLoadWithRequest={shouldStartLoadWithRequest}
            accessible={true}
            accessibilityLabel={sanitizedHTML}
            onError={(syntheticEvent) => {
              const { nativeEvent } = syntheticEvent;
              logError("webview_render_failed", {
                message: nativeEvent.description,
              });
            }}
            onMessage={(event) => {
              const height = Number(event.nativeEvent.data);
              if (!isNaN(height)) {
                setWebViewHeight(height);
              }
            }}
          />
        </View>
      ) : (
        <Text style={combinedStyle} accessible={true} accessibilityRole="text" maxFontSizeMultiplier={1.2}>
          {sanitizedHTML}
        </Text>
      )}
    </View>
  );
};

export default ContainsHTML;
